import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { PosStore } from "@/utils/posStores";

/**
 * Reads the full cross-brand store registry (spec 2.1). This is a GLOBAL
 * reference table, not tenant data, so it is intentionally NOT scoped to the
 * user's store_id: any signed-in operator sees every store. Reading it per
 * mount (rather than baking a build-time constant) is how the "hot reload on
 * change" guarantee is met: edit the rows and the next load reflects them.
 */
export const usePosStores = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<PosStore[]>({
    queryKey: ["pos-stores"],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");

      // pos_stores is not in the generated Database types yet, so go through an
      // untyped client (same pattern as useSalesInsights / useRevenuePlays).
      const sb = supabase as unknown as SupabaseClient;

      // A plain .select() is capped by PostgREST's max-rows setting and returns
      // a silently truncated registry, which would make later locations
      // unfindable in the typeahead. Page explicitly until a short page comes
      // back (same reason scripts/import-pos-stores.mjs pages its snapshot).
      const PAGE = 1000;
      const rows: PosStore[] = [];
      for (let offset = 0; ; ) {
        const { data, error } = await sb
          .from("pos_stores")
          .select("id, brand, store_id, location_name, available")
          .order("location_name", { ascending: true })
          .order("store_id", { ascending: true })
          .range(offset, offset + PAGE - 1);
        if (error) throw error;

        const page = (data ?? []) as PosStore[];
        if (page.length === 0) break;
        rows.push(...page);
        offset += page.length;
      }

      return rows;
    },
    staleTime: 5 * 60 * 1000,
  });
};
